/**
 * 8D Investigation Report — Read-only view of incident data in 8D format.
 *
 * Fetches from /api/hazards/{hazardId}/eight-d and renders D1-D8 sections
 * as collapsible cards with a completeness indicator and PDF export.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  FileText, Download, RefreshCw, ChevronDown, ChevronRight,
  Users, AlertTriangle, Shield, Search, CheckCircle, Wrench,
  ClipboardCheck, Award, Upload, Trash2, Paperclip
} from 'lucide-react';
import { usePermissions } from '../context/PermissionContext';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const SECTION_CONFIG = [
  { key: 'd1_team', num: 1, title: 'Team Formation', icon: Users, color: 'blue' },
  { key: 'd2_problem_description', num: 2, title: 'Problem Description', icon: AlertTriangle, color: 'red' },
  { key: 'd3_containment', num: 3, title: 'Interim Containment Actions', icon: Shield, color: 'orange' },
  { key: 'd4_root_cause', num: 4, title: 'Root Cause Analysis', icon: Search, color: 'purple' },
  { key: 'd5_corrective_actions', num: 5, title: 'Corrective Actions', icon: Wrench, color: 'green' },
  { key: 'd6_validation', num: 6, title: 'Implementation Verification', icon: CheckCircle, color: 'teal' },
  { key: 'd7_preventive_actions', num: 7, title: 'Preventive Actions', icon: ClipboardCheck, color: 'indigo' },
  { key: 'd8_closure', num: 8, title: 'Closure & Recognition', icon: Award, color: 'gray' },
];

const STATUS_COLORS = {
  'Open': 'bg-yellow-100 text-yellow-800',
  'In-Progress': 'bg-blue-100 text-blue-800',
  'Closed': 'bg-green-100 text-green-800',
  'Draft': 'bg-gray-100 text-gray-600',
  'Pending Approval': 'bg-orange-100 text-orange-700',
  'Pending Verification': 'bg-purple-100 text-purple-700',
};

// REG-07: tenant date format + timezone (dateUtils) instead of hardcoded en-GB.
// Unparseable values are shown as-is, as before.
const formatDisplayDate = (val) => {
  if (!val) return '—';
  try {
    if (isNaN(new Date(val).getTime())) return val;
    const out = formatDate(val);
    return out === '—' ? val : out;
  } catch { return val; }
};

const formatDisplayDateTime = (val) => {
  if (!val) return '—';
  try {
    if (isNaN(new Date(val).getTime())) return val;
    const out = formatDateTime(val);
    return out === '—' ? val : out;
  } catch { return val; }
};

const EightDReport = ({ hazardId, canUpload = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [expanded, setExpanded] = useState(['d2_problem_description', 'd4_root_cause']);
  const [uploadingD6, setUploadingD6] = useState(false);
  const [d6UploadError, setD6UploadError] = useState(null);
  const { hasPermission } = usePermissions();

  useEffect(() => { fetchData(); }, [hazardId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/incidents/${hazardId}/eight-d`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load 8D report data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API}/incidents/${hazardId}/eight-d/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `8D_Report_${(data?.incident_number || hazardId).replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  const toggleSection = (key) => {
    setExpanded(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // --- D6 8D-closure document handlers ---
  const handleD6Upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setD6UploadError(null);
    setUploadingD6(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      formData.append('eight_d_section', 'd6');
      const res = await axios.post(
        `${API}/incidents/${hazardId}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data?.errors?.length) {
        setD6UploadError(res.data.errors.join('; '));
      }
      await fetchData();
    } catch (err) {
      setD6UploadError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingD6(false);
      // Reset the input so the same file can be re-selected if needed
      if (event.target) event.target.value = '';
    }
  };

  const handleD6Download = async (docIndex) => {
    try {
      const res = await axios.get(`${API}/incidents/${hazardId}/documents/${docIndex}/download`);
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to get download URL');
    }
  };

  const handleD6Delete = async (docIndex) => {
    if (!window.confirm('Delete this 8D closure document?')) return;
    try {
      await axios.delete(`${API}/incidents/${hazardId}/documents/${docIndex}`);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  const statusBadge = (status) => {
    const cls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${cls}`}>{status || '—'}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1B5C94]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
        <button onClick={fetchData} className="mt-3 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const comp = data.completeness || {};

  // --- Section Renderers ---

  const renderD1 = () => {
    const d = data.d1_team;
    const members = (d.team_members || []).map(m => typeof m === 'string' ? m : m.name || m.user_name || '').filter(Boolean);
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div><span className="text-xs text-gray-500 font-semibold">Lead Investigator</span><p className="font-medium">{d.lead_investigator || <span className="text-gray-400 italic">Not assigned</span>}</p></div>
          <div><span className="text-xs text-gray-500 font-semibold">Assigned To</span><p className="font-medium">{d.assignee || '—'}</p></div>
        </div>
        <div><span className="text-xs text-gray-500 font-semibold">Reported By</span><p className="font-medium">{d.reported_by || '—'}</p></div>
        {members.length > 0 && (
          <div>
            <span className="text-xs text-gray-500 font-semibold">Team Members</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {members.map((m, i) => <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">{m}</span>)}
            </div>
          </div>
        )}
        {!d.lead_investigator && members.length === 0 && (
          <p className="text-gray-400 italic text-sm">No investigation team has been assigned yet. Create an investigation to populate this section.</p>
        )}
      </div>
    );
  };

  const renderD2 = () => {
    const d = data.d2_problem_description;
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div><span className="text-xs text-gray-500 font-semibold">Incident ID</span><p className="font-mono font-medium">{d.incident_number || '—'}</p></div>
          <div><span className="text-xs text-gray-500 font-semibold">Type</span><p className="font-medium">{d.hazard_type || '—'}</p></div>
          <div><span className="text-xs text-gray-500 font-semibold">Severity</span><p>{statusBadge(d.severity)}</p></div>
          <div><span className="text-xs text-gray-500 font-semibold">Location</span><p className="font-medium">{d.location || '—'}</p></div>
          <div><span className="text-xs text-gray-500 font-semibold">Date</span><p className="font-medium">{formatDisplayDate(d.incident_date)}</p></div>
          <div><span className="text-xs text-gray-500 font-semibold">Equipment</span><p className="font-medium">{d.equipment_involved || '—'}</p></div>
        </div>
        {(d.injury_count > 0 || d.fatality_count > 0 || d.is_lti) && (
          <div className="flex gap-4 mt-2">
            {d.injury_count > 0 && <span className="text-sm font-semibold text-orange-600">Injuries: {d.injury_count}</span>}
            {d.fatality_count > 0 && <span className="text-sm font-semibold text-red-700">Fatalities: {d.fatality_count}</span>}
            {d.is_lti && <span className="text-sm font-semibold text-red-600">LTI ({d.medical_leave_days || 0} days)</span>}
          </div>
        )}
        <div className="mt-2">
          <span className="text-xs text-gray-500 font-semibold">Description</span>
          <p className="text-gray-700 mt-1">{d.description || '—'}</p>
        </div>
      </div>
    );
  };

  const renderD3 = () => {
    const d = data.d3_containment;
    return d.immediate_action
      ? <p className="text-gray-700">{d.immediate_action}</p>
      : <p className="text-gray-400 italic text-sm">No containment action recorded.</p>;
  };

  const renderD4 = () => {
    const d = data.d4_root_cause;
    const fw = d.five_why;
    const fb = d.fishbone;
    const hasAny = d.root_cause || fw || fb || d.investigation_root_causes?.length > 0;
    if (!hasAny) return <p className="text-gray-400 italic text-sm">No root cause analysis data available. Use 5-Why or Fishbone tools from the Details tab.</p>;

    return (
      <div className="space-y-4">
        {d.root_cause && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <span className="text-xs text-yellow-700 font-bold">Primary Root Cause</span>
            <p className="text-gray-800 mt-1">{d.root_cause}</p>
          </div>
        )}
        {fw && fw.whys && fw.whys.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-bold text-sm text-gray-800 mb-2">Five-Why Analysis</h4>
            {fw.problem_statement && <p className="text-sm text-gray-600 mb-2"><b>Problem:</b> {fw.problem_statement}</p>}
            <ol className="list-decimal list-inside space-y-1.5">
              {fw.whys.map((w, i) => (
                <li key={i} className="text-sm text-gray-700">
                  {typeof w === 'string' ? w : <><b>{w.question || w.why || `Why ${i+1}`}</b> — {w.answer || ''}</>}
                </li>
              ))}
            </ol>
            {fw.root_cause && <p className="mt-2 text-sm font-semibold text-green-700">Root Cause: {fw.root_cause}</p>}
          </div>
        )}
        {fb && fb.categories && (
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-bold text-sm text-gray-800 mb-2">Fishbone (Ishikawa) Analysis</h4>
            {fb.problem_statement && <p className="text-sm text-gray-600 mb-2"><b>Problem:</b> {fb.problem_statement}</p>}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                ['man', 'Man (People)', 'blue'], ['machine', 'Machine (Equipment)', 'orange'],
                ['method', 'Method (Process)', 'purple'], ['material', 'Material', 'green'],
                ['measurement', 'Measurement', 'cyan'], ['environment', 'Environment', 'teal'],
              ].map(([key, label, color]) => {
                const items = fb.categories[key] || [];
                if (!items.length) return null;
                return (
                  <div key={key} className={`bg-${color}-50 rounded-lg p-2`}>
                    <span className={`text-xs font-bold text-${color}-700`}>{label}</span>
                    <ul className="mt-1 space-y-0.5">
                      {items.map((item, i) => <li key={i} className="text-xs text-gray-700">• {item}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
            {fb.root_cause && <p className="mt-2 text-sm font-semibold text-green-700">Root Cause: {fb.root_cause}</p>}
          </div>
        )}
        {d.investigation_root_causes?.length > 0 && (
          <div>
            <h4 className="font-bold text-sm text-gray-800 mb-1">Investigation Root Causes</h4>
            <ul className="space-y-1">{d.investigation_root_causes.map((rc, i) => <li key={i} className="text-sm text-gray-700">• {rc}</li>)}</ul>
          </div>
        )}
        {d.contributing_factors?.length > 0 && (
          <div>
            <h4 className="font-bold text-sm text-gray-800 mb-1">Contributing Factors</h4>
            <ul className="space-y-1">{d.contributing_factors.map((cf, i) => <li key={i} className="text-sm text-gray-700">• {cf}</li>)}</ul>
          </div>
        )}
      </div>
    );
  };

  const renderCapaTable = (capas) => {
    if (!capas || capas.length === 0) return <p className="text-gray-400 italic text-sm">No actions recorded.</p>;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50">
            <th className="px-3 py-2 text-left font-semibold text-gray-600">Title</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-600">Responsible</th>
            <th className="px-3 py-2 text-center font-semibold text-gray-600">Status</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-600">Due Date</th>
          </tr></thead>
          <tbody>
            {capas.map((c, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-3 py-2 font-medium text-gray-800">{c.title || '—'}</td>
                <td className="px-3 py-2 text-gray-700">{c.responsible_person || '—'}</td>
                <td className="px-3 py-2 text-center">{statusBadge(c.status)}</td>
                <td className="px-3 py-2 text-gray-700">{formatDisplayDate(c.target_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderD5 = () => renderCapaTable(data.d5_corrective_actions);

  const renderD6 = () => {
    const d6 = data.d6_validation;
    const d6Docs = data.d6_documents || [];
    const hasVerifications = d6 && d6.length > 0;
    const hasDocs = d6Docs.length > 0;

    return (
      <div className="space-y-4">
        {/* CAPA verifications table (existing behaviour) */}
        {hasVerifications ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50">
                <th className="px-3 py-2 text-left font-semibold text-gray-600">CAPA</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Verified By</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Result</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-600">Effective</th>
              </tr></thead>
              <tbody>
                {d6.map((v, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium text-gray-800">{v.capa_title || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{v.verified_by || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{formatDisplayDate(v.verification_date)}</td>
                    <td className="px-3 py-2 text-gray-700">{v.verification_result || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {v.is_effective === true ? <span className="text-green-600 font-bold">Yes</span>
                        : v.is_effective === false ? <span className="text-red-600 font-bold">No</span>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !hasDocs && <p className="text-gray-400 italic text-sm">No verification data available yet.</p>
        )}

        {/* 8D closure documentation — upload + list */}
        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">
                8D Closure Documentation {hasDocs && <span className="text-gray-400 font-normal">({d6Docs.length})</span>}
              </span>
            </div>
            {canUpload && (
              <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md cursor-pointer transition-colors ${
                uploadingD6
                  ? 'bg-gray-100 text-gray-400 cursor-wait'
                  : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'
              }`}>
                <Upload className="w-3.5 h-3.5" />
                {uploadingD6 ? 'Uploading…' : 'Attach File'}
                <input
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  className="hidden"
                  onChange={handleD6Upload}
                  disabled={uploadingD6}
                />
              </label>
            )}
          </div>

          {d6UploadError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-2">
              {d6UploadError}
            </div>
          )}

          {hasDocs ? (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md bg-white">
              {d6Docs.map((doc) => (
                <li key={doc.index} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 truncate">{doc.filename || '—'}</p>
                      <p className="text-xs text-gray-500">
                        {doc.uploaded_by_name || 'Unknown'} · {formatDisplayDate(doc.uploaded_at)}
                        {doc.size_bytes ? ` · ${(doc.size_bytes / 1024).toFixed(0)} KB` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleD6Download(doc.index)}
                      className="p-1 text-gray-500 hover:text-teal-600"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {canUpload && (
                      <button
                        onClick={() => handleD6Delete(doc.index)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-400 italic">
              {canUpload
                ? 'Attach files (verification evidence, sign-off documents, photos) to document 8D closure.'
                : 'No closure documents uploaded.'}
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderD7 = () => {
    const d = data.d7_preventive_actions;
    const rec = d.recommended_actions || [];
    return (
      <div className="space-y-3">
        {renderCapaTable(d.capas)}
        {rec.length > 0 && (
          <div>
            <h4 className="font-bold text-sm text-gray-800 mb-1">Recommended Actions</h4>
            <ul className="space-y-1">{rec.map((a, i) => <li key={i} className="text-sm text-gray-700">• {a}</li>)}</ul>
          </div>
        )}
      </div>
    );
  };

  const renderD8 = () => {
    const d = data.d8_closure;
    const isOpen = d.status !== 'Closed';
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div><span className="text-xs text-gray-500 font-semibold">Status</span><p>{statusBadge(d.status)}</p></div>
          <div><span className="text-xs text-gray-500 font-semibold">Closed Date</span><p className="font-medium">{formatDisplayDateTime(d.closed_date)}</p></div>
        </div>
        {d.close_notes && <div><span className="text-xs text-gray-500 font-semibold">Close Notes</span><p className="text-gray-700 mt-1">{d.close_notes}</p></div>}
        {d.conclusions?.length > 0 && (
          <div><h4 className="font-bold text-sm text-gray-800 mb-1">Conclusions</h4>
            <ul className="space-y-1">{d.conclusions.map((c, i) => <li key={i} className="text-sm text-gray-700">• {typeof c === 'string' ? c : JSON.stringify(c)}</li>)}</ul>
          </div>
        )}
        {d.lessons_learned?.length > 0 && (
          <div><h4 className="font-bold text-sm text-gray-800 mb-1">Lessons Learned</h4>
            <ul className="space-y-1">{d.lessons_learned.map((l, i) => <li key={i} className="text-sm text-gray-700">• {typeof l === 'string' ? l : JSON.stringify(l)}</li>)}</ul>
          </div>
        )}
        {isOpen && !d.close_notes && <p className="text-gray-400 italic text-sm">This incident has not been closed yet.</p>}
      </div>
    );
  };

  const renderers = { d1_team: renderD1, d2_problem_description: renderD2, d3_containment: renderD3, d4_root_cause: renderD4, d5_corrective_actions: renderD5, d6_validation: renderD6, d7_preventive_actions: renderD7, d8_closure: renderD8 };

  return (
    <div className="space-y-4 mt-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-[#1B5C94]" />
          <div>
            <h2 className="text-lg font-bold text-gray-800">8D Investigation Report</h2>
            <p className="text-xs text-gray-500">Incident: {data.incident_number || data.hazard_id?.substring(0, 8)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          {hasPermission('incidents', 'export') && (
            <button onClick={handleExportPdf} disabled={exportingPdf}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#1B5C94] text-white rounded-lg text-sm font-semibold hover:bg-[#144a78] disabled:opacity-50">
              <Download className="w-4 h-4" />
              {exportingPdf ? 'Exporting...' : 'Export 8D PDF'}
            </button>
          )}
        </div>
      </div>

      {/* Completeness bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700">Completeness</span>
          <span className="text-sm font-bold text-[#1B5C94]">{comp.score || 0} / {comp.total || 8}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div className="bg-[#1B5C94] h-2.5 rounded-full transition-all" style={{ width: `${((comp.score || 0) / (comp.total || 8)) * 100}%` }} />
        </div>
        {comp.missing?.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">Missing: {comp.missing.join(', ')}</p>
        )}
      </div>

      {/* 8 Section Cards */}
      {SECTION_CONFIG.map(sec => {
        const isExpanded = expanded.includes(sec.key);
        const isFilled = (comp.filled || []).includes(`D${sec.num}`);
        const Icon = sec.icon;
        return (
          <div key={sec.key} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button onClick={() => toggleSection(sec.key)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                <div className="w-8 h-8 rounded-lg bg-[#1B5C94] flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{sec.num}</span>
                </div>
                <span className="font-semibold text-gray-800">D{sec.num} — {sec.title}</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isFilled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {isFilled ? 'Complete' : 'Incomplete'}
              </span>
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                {renderers[sec.key]()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EightDReport;
